import { useEffect, useRef } from 'react';

const commitments = [
  { icon: 'ri-recycle-line', title: 'Responsible Sourcing', desc: 'Supporting ethical procurement practices aligned with evolving environmental standards and international trade regulations.' },
  { icon: 'ri-leaf-line', title: 'Sustainable Supply Chains', desc: 'Committed to reducing environmental impact through thoughtful logistics coordination and supply chain optimization.' },
  { icon: 'ri-building-4-line', title: 'Economic Development', desc: 'Contributing to sustainable economic growth by providing essential raw materials that enable infrastructure and manufacturing.' },
  { icon: 'ri-seedling-line', title: 'Agricultural Supply Chains', desc: 'Supporting global food security by facilitating efficient and transparent trade in agricultural commodities and fertilizers.' },
];

export default function SustainabilityContact() {
  const sectionRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.querySelectorAll('.reveal-item').forEach((el, i) => {
              setTimeout(() => {
                (el as HTMLElement).style.opacity = '1';
                (el as HTMLElement).style.transform = 'translateY(0)';
              }, i * 120);
            });
          }
        });
      },
      { threshold: 0.1 }
    );
    if (sectionRef.current) observer.observe(sectionRef.current);
    return () => observer.disconnect();
  }, []);

  return (
    <>
      {/* Sustainability Section */}
      <section id="sustainability" className="relative py-28 overflow-hidden" ref={sectionRef}>
        <div className="absolute inset-0 z-0">
          <img
            src="https://public.readdy.ai/ai/img_res/4fd2f71e99bddbdd4ee1a735ca5b8e59.jpg"
            alt="Sustainability"
            className="w-full h-full object-cover object-center"
          />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0A1520]/70 via-[#0D1B2A]/75 to-[#0A1520]/85" />
        </div>

        <div className="relative z-10 max-w-[1400px] mx-auto px-6 md:px-10">
          <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-4">
            <span className="inline-block border border-[#888888]/40 rounded-full px-6 py-2 text-[#A0A0A0] text-xs uppercase tracking-[0.3em]">
              Our Commitment
            </span>
          </div>
          <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-5">
            <h2 className="text-4xl md:text-5xl font-playfair text-white max-w-2xl">Sustainability <em className="text-[#A0A0A0]">Commitment</em></h2>
          </div>
          <div className="reveal-item opacity-0 translate-y-8 transition-all duration-700 mb-14">
            <p className="text-[#B0B0B0] text-lg max-w-2xl leading-relaxed">
              Bulk Ore supports responsible sourcing and ethical trade practices aligned with evolving environmental and regulatory standards. We aim to contribute to sustainable development and global agricultural supply chains through continuous improvement and responsible engagement with partners.
            </p>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-5">
            {commitments.map((c, i) => (
              <div key={i} className="reveal-item opacity-0 translate-y-8 transition-all duration-700 bg-white/10 backdrop-blur-sm rounded-2xl p-6 border border-white/15 hover:bg-white/15 transition-all duration-300">
                <div className="w-11 h-11 flex items-center justify-center rounded-xl bg-[#4A4A4A]/30 border border-[#888888]/40 mb-5">
                  <i className={`${c.icon} text-[#A0A0A0] text-xl`} />
                </div>
                <h4 className="font-semibold text-white text-sm mb-2">{c.title}</h4>
                <p className="text-[#B0B0B0] text-xs leading-relaxed">{c.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section id="contact" className="bg-[#1A1A1A] py-28">
        <div className="max-w-[1400px] mx-auto px-6 md:px-10 text-center">
          <span className="inline-block border border-[#888888]/40 rounded-full px-6 py-2 text-[#A0A0A0] text-xs uppercase tracking-[0.3em] mb-8">
            Get In Touch
          </span>
          <h2 className="text-4xl md:text-5xl font-playfair text-white mb-6 leading-tight">
            Let's Build a <em className="text-[#A0A0A0]">Partnership</em>
          </h2>
          <p className="text-[#999999] text-lg leading-relaxed max-w-xl mx-auto mb-12">
            For all questions and inquiries, please reach out to us directly and our team will get back to you promptly.
          </p>
          <div className="flex flex-col sm:flex-row items-center justify-center gap-5 flex-wrap">
            {/* Address */}
            <div className="inline-flex items-center gap-5 bg-[#252525] border border-[#888888]/20 rounded-2xl px-8 py-6 hover:border-[#888888]/50 transition-all duration-300">
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#4A4A4A]/30 border border-[#888888]/30 flex-shrink-0">
                <i className="ri-map-pin-2-line text-[#A0A0A0] text-xl" />
              </div>
              <div className="text-left">
                <div className="text-[#999999] text-xs uppercase tracking-widest mb-1">Address</div>
                <div className="text-white text-sm font-medium leading-relaxed">
                  4/F Lee Gardens Three, 1 Sunning Road,<br />Causeway Bay, Hong Kong SAR
                </div>
              </div>
            </div>
            {/* Tel */}
            <a
              href="tel:+85239606380"
              className="inline-flex items-center gap-5 bg-[#252525] border border-[#888888]/20 rounded-2xl px-8 py-6 hover:border-[#888888]/50 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#4A4A4A]/30 border border-[#888888]/30 flex-shrink-0">
                <i className="ri-phone-line text-[#A0A0A0] text-xl" />
              </div>
              <div className="text-left">
                <div className="text-[#999999] text-xs uppercase tracking-widest mb-1">Tel</div>
                <span className="text-white text-lg font-semibold group-hover:text-[#CCCCCC] transition-colors duration-300">
                  +852 3960 6380
                </span>
              </div>
            </a>
            {/* Email */}
            <a
              href="mailto:info@bulk-ore.com"
              className="inline-flex items-center gap-5 bg-[#252525] border border-[#888888]/20 rounded-2xl px-8 py-6 hover:border-[#888888]/50 transition-all duration-300 group cursor-pointer"
            >
              <div className="w-12 h-12 flex items-center justify-center rounded-xl bg-[#4A4A4A]/30 border border-[#888888]/30 flex-shrink-0">
                <i className="ri-mail-line text-[#A0A0A0] text-xl" />
              </div>
              <div className="text-left">
                <div className="text-[#999999] text-xs uppercase tracking-widest mb-1">Email Us</div>
                <span className="text-white text-lg font-semibold group-hover:text-[#CCCCCC] transition-colors duration-300">
                  info@bulk-ore.com
                </span>
              </div>
            </a>
          </div>
        </div>
      </section>
    </>
  );
}
